import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  createTemplate,
  deleteTemplate,
  getTemplateById,
  getTemplates,
  updateTemplate,
} from "../../utils/adminApi";

const EMPTY_FORM = {
  id: null,
  name: "",
  thumbnail_url: "",
  json_structure: '{\n  "version": 1,\n  "blocks": []\n}',
  premium: false,
  price: 0,
};

function AdminTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const modeLabel = useMemo(
    () => (form.id ? "Edit template" : "Create template"),
    [form.id]
  );

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно вчитување на шаблони.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    let parsedJsonStructure;
    try {
      parsedJsonStructure = JSON.parse(form.json_structure);
    } catch {
      setError("`json_structure` мора да биде валиден JSON.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      thumbnail_url: form.thumbnail_url.trim(),
      json_structure: parsedJsonStructure,
      premium: Boolean(form.premium),
      price: Number(form.price) || 0,
    };

    if (!payload.name || !payload.thumbnail_url) {
      setError("Name и Thumbnail URL се задолжителни.");
      return;
    }

    try {
      setSubmitting(true);
      if (form.id) {
        await updateTemplate(form.id, payload);
        setSuccess("Template успешно ажуриран.");
      } else {
        await createTemplate(payload);
        setSuccess("Template успешно креиран.");
      }
      setForm(EMPTY_FORM);
      await loadTemplates();
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно зачувување на шаблон.");
    } finally {
      setSubmitting(false);
    }
  };

  const onEdit = async (templateId) => {
    setError("");
    setSuccess("");
    try {
      const template = await getTemplateById(templateId);
      setForm({
        id: template.id || templateId,
        name: template.name || "",
        thumbnail_url: template.thumbnail_url || "",
        json_structure: JSON.stringify(
          template.json_structure || { version: 1, blocks: [] },
          null,
          2
        ),
        premium: Boolean(template.premium),
        price: Number(template.price) || 0,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно вчитување на шаблон.");
    }
  };

  const onDelete = async (templateId) => {
    const confirmed = window.confirm(
      "Сигурни сте дека сакате да го избришете template-от?"
    );
    if (!confirmed) return;

    setError("");
    setSuccess("");
    try {
      await deleteTemplate(templateId);
      setSuccess("Template успешно избришан.");
      if (form.id === templateId) {
        setForm(EMPTY_FORM);
      }
      await loadTemplates();
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно бришење на шаблон.");
    }
  };

  return (
    <Card className="admin-card">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mb={2}
        >
          <Typography variant="h6">Invitation Templates</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={loadTemplates}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

        <Box component="form" onSubmit={onSubmit} className="admin-template-form">
          <Typography variant="subtitle1" mb={1}>
            {modeLabel}
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              size="small"
              required
            />
            <TextField
              label="Thumbnail URL"
              value={form.thumbnail_url}
              onChange={(e) => handleChange("thumbnail_url", e.target.value)}
              size="small"
              required
            />
            <TextField
              label="JSON Structure"
              value={form.json_structure}
              onChange={(e) => handleChange("json_structure", e.target.value)}
              multiline
              minRows={14}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Price"
                type="number"
                value={form.price}
                onChange={(e) => handleChange("price", e.target.value)}
                size="small"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.premium}
                    onChange={(e) => handleChange("premium", e.target.checked)}
                  />
                }
                label="Premium"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button type="submit" variant="contained" disabled={submitting}>
                {form.id ? "Update Template" : "Create Template"}
              </Button>
              <Button
                type="button"
                variant="text"
                disabled={submitting}
                onClick={() => {
                  setForm(EMPTY_FORM);
                  setError("");
                  setSuccess("");
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Box>

        {loading ? (
          <Typography color="text.secondary" mt={2}>
            Loading templates...
          </Typography>
        ) : (
          <Box className="admin-table-wrap" mt={2}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Premium</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template, index) => (
                  <TableRow key={template.id || index}>
                    <TableCell>{template.id || "-"}</TableCell>
                    <TableCell>{template.name || "-"}</TableCell>
                    <TableCell>{template.premium ? "Yes" : "No"}</TableCell>
                    <TableCell>{template.price ?? 0}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          onClick={() => onEdit(template.id)}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => onDelete(template.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>Нема шаблони.</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default AdminTemplatesSection;
